import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import MintBatchDialog from '@/components/tap/MintBatchDialog.jsx';
import useTapBatches from '@/hooks/useTapBatches.js';
import { formatDate } from '@/lib/date-display.js';

const fmtDate = (iso) => formatDate(iso, { month: 'short', day: 'numeric', year: '2-digit' });
function dateRange(b) {
  const a = fmtDate(b.first_issued_at);
  const z = fmtDate(b.last_issued_at);
  return a === z ? a : `${a} – ${z}`;
}

function StatTile({ label, value }) {
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="text-2xl font-semibold tabular-nums text-text">{value}</div>
      <div className="mt-0.5 text-xs text-text-muted">{label}</div>
    </div>
  );
}

function sumField(batches, f) {
  return (batches || []).reduce((acc, b) => acc + (b[f] || 0), 0);
}

export default function TapAdmin() {
  const navigate = useNavigate();
  const { batches, error, loading, refresh } = useTapBatches();
  const [mintOpen, setMintOpen] = useState(false);

  const stats = useMemo(() => ({
    minted: sumField(batches, 'total'),
    programmed: sumField(batches, 'stat_programmed_not_handed'),
    handed: sumField(batches, 'stat_handed_not_tapped'),
    registered: sumField(batches, 'registered'),
    tappedWeek: sumField(batches, 'stat_tapped_this_week'),
    revoked: sumField(batches, 'revoked'),
  }), [batches]);

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text">TAP Keychains</h2>
          <p className="mt-1 text-sm text-text-muted">NFC tokens · batches · handoff log</p>
        </div>
        <Button onClick={() => setMintOpen(true)}>
          <Plus size={16} strokeWidth={1.5} /> Mint batch
        </Button>
      </div>

      {/* Stats row */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Minted" value={stats.minted} />
        <StatTile label="Programmed" value={stats.programmed} />
        <StatTile label="Handed out" value={stats.handed} />
        <StatTile label="Registered" value={stats.registered} />
        <StatTile label="Tapped this week" value={stats.tappedWeek} />
        <StatTile label="Revoked" value={stats.revoked} />
      </div>

      {/* Batches */}
      <div className="mt-6">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Batches</h3>

        {loading && !batches && (
          <div className="rounded-card border border-border bg-surface p-6 text-sm text-text-muted">Loading…</div>
        )}
        {error && !batches && (
          <div className="rounded-card border border-accent/40 bg-accent-soft p-4 text-sm text-accent">
            Couldn't load batches: {error.code || error.message}
          </div>
        )}
        {batches && batches.length === 0 && (
          <div className="rounded-card border border-dashed border-border p-6 text-center text-sm text-text-muted">
            No batches yet. Mint one to get started.
          </div>
        )}

        {batches && batches.length > 0 && (
          <div className="grid gap-2">
            {batches.map((b) => (
              <button
                key={b.batch_label}
                type="button"
                onClick={() => navigate(`/admin/tap/batch/${encodeURIComponent(b.batch_label)}`)}
                className="flex items-center gap-3 rounded-card border border-border bg-surface p-3 text-left hover:border-border-strong"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-text">{b.batch_label}</div>
                  <div className="mt-0.5 text-xs text-text-dim">{dateRange(b)}</div>
                </div>
                <div className="hidden gap-4 text-xs text-text-muted sm:flex">
                  <span><span className="text-text">{b.total}</span> total</span>
                  <span><span className="text-text">{b.programmed}</span> prog</span>
                  <span><span className="text-text">{b.handed_out}</span> out</span>
                  <span><span className="text-text">{b.registered}</span> reg</span>
                  {b.revoked > 0 && <span className="text-text-dim">{b.revoked} revoked</span>}
                </div>
                <ChevronRight size={18} strokeWidth={1.5} className="shrink-0 text-text-dim" />
              </button>
            ))}
          </div>
        )}
      </div>

      <MintBatchDialog open={mintOpen} onOpenChange={setMintOpen} onMinted={refresh} />
    </div>
  );
}
